/* Voice in / voice out.
 *
 * We use MediaRecorder for capture (it's well-supported, gives us an
 * already-encoded webm/opus stream that Whisper happily ingests), and a plain
 * <audio> element for playback. Streaming TTS is not in scope for v1 — we
 * fetch the whole audio once the AI message is complete.
 *
 * Permissions: getUserMedia prompts the OS dialog. We surface a friendly
 * preamble in the UI before calling this so the user understands the prompt.
 */

import { API_BASE, apiClient } from './api';
import { loadSession } from './auth';

export interface RecordingHandle {
  /** Stop recording and resolve with the final blob. */
  stop: () => Promise<Blob>;
  /** Cancel without resolving. */
  cancel: () => void;
  /** Subscribe to chunked audio data (~250 ms chunks). */
  onChunk: (cb: (chunk: Blob) => void) => () => void;
  /** True while the underlying MediaRecorder is recording. */
  isActive: () => boolean;
}

const PREFERRED_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4', // Safari
];

function pickMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  for (const m of PREFERRED_MIMES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return undefined;
}

export async function recordAudio(): Promise<RecordingHandle> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Microphone access is not available in this browser.');
  }
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
      sampleRate: 16000,
    },
  });

  const mimeType = pickMime();
  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

  const chunks: Blob[] = [];
  const chunkSubscribers = new Set<(b: Blob) => void>();
  let active = true;

  recorder.addEventListener('dataavailable', (ev) => {
    if (ev.data && ev.data.size > 0) {
      chunks.push(ev.data);
      for (const cb of chunkSubscribers) cb(ev.data);
    }
  });

  // Stream chunks every 250ms so we *could* upload mid-recording. v1 buffers.
  recorder.start(250);

  const releaseStream = () => {
    active = false;
    for (const t of stream.getTracks()) t.stop();
  };

  return {
    stop: () =>
      new Promise<Blob>((resolve, reject) => {
        if (recorder.state === 'inactive') {
          releaseStream();
          resolve(new Blob(chunks, { type: mimeType ?? 'audio/webm' }));
          return;
        }
        recorder.addEventListener(
          'stop',
          () => {
            releaseStream();
            resolve(new Blob(chunks, { type: mimeType ?? 'audio/webm' }));
          },
          { once: true },
        );
        recorder.addEventListener('error', (ev) => reject(ev), { once: true });
        recorder.stop();
      }),
    cancel: () => {
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        /* swallow */
      }
      releaseStream();
    },
    onChunk: (cb) => {
      chunkSubscribers.add(cb);
      return () => chunkSubscribers.delete(cb);
    },
    isActive: () => active,
  };
}

/** Send a recording to Whisper for transcription. Returns the transcript. */
export async function transcribe(blob: Blob): Promise<{ text: string }> {
  const form = new FormData();
  form.append('audio', blob, 'recording.webm');
  return apiClient.post<{ text: string }>('/api/v1/voice/transcribe', form);
}

/** Fetch TTS audio for `text` and play it. Returns a stop() handle. */
export async function playTTS(
  text: string,
  opts: { voice?: string } = {},
): Promise<{ stop: () => void; finished: Promise<void> }> {
  const url = (API_BASE ? API_BASE : '/api') + '/voice/tts';
  const session = loadSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session) headers.Authorization = `Bearer ${session.token}`;

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ text, voice: opts.voice }),
  });
  if (!res.ok) {
    throw new Error(`TTS request failed: ${res.status}`);
  }
  const audioBlob = await res.blob();
  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioUrl);
  audio.preload = 'auto';

  const finished = new Promise<void>((resolve) => {
    const cleanup = () => {
      URL.revokeObjectURL(audioUrl);
      resolve();
    };
    audio.addEventListener('ended', cleanup, { once: true });
    audio.addEventListener('error', cleanup, { once: true });
  });

  await audio.play();

  return {
    stop: () => {
      audio.pause();
      audio.currentTime = 0;
      URL.revokeObjectURL(audioUrl);
    },
    finished,
  };
}
